import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { AiRunActivityService } from './ai-run-activity.service';

/**
 * Indicador do rodapé: gira enquanto uma execução de IA do próprio usuário roda; ao terminar mostra
 * um ok (ou um aviso, se falhou) por alguns segundos e some.
 */
@Component({
  selector: 'app-ai-run-activity',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (activity.runningCount() > 0) {
      <span class="ai-run-activity" role="status" [title]="runningTitle()">
        <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
        <span class="visually-hidden">{{ runningTitle() }}</span>
      </span>
      <span class="mx-2">|</span>
    } @else if (activity.flash(); as flash) {
      <span class="ai-run-activity" role="status" [title]="flashTitle()">
        <i
          class="pi"
          [class.pi-check-circle]="flash.kind === 'completed'"
          [class.pi-exclamation-circle]="flash.kind === 'failed'"
          [style.color]="
            flash.kind === 'completed'
              ? 'var(--state-success, #16a34a)'
              : 'var(--state-danger, #dc2626)'
          "
          aria-hidden="true"></i>
        <span class="visually-hidden">{{ flashTitle() }}</span>
      </span>
      <span class="mx-2">|</span>
    }
  `,
  styles: [
    `
      :host {
        display: contents;
      }
      .ai-run-activity {
        display: inline-flex;
        align-items: center;
        vertical-align: middle;
      }
    `,
  ],
})
export class AiRunActivityComponent {
  protected readonly activity = inject(AiRunActivityService);

  /** Uma linha por execução: "Pipeline — executando… (etapa)". */
  protected readonly runningTitle = computed(() =>
    this.activity
      .running()
      .map(run => {
        const status = run.status === 'running' ? 'executando…' : 'na fila…';
        return `${run.pipelineName ?? 'Pipeline de IA'} — ${status}${run.step ? ` (${run.step})` : ''}`;
      })
      .join('\n')
  );

  protected readonly flashTitle = computed(() => {
    const flash = this.activity.flash();
    if (!flash) return '';
    return `${flash.pipelineName ?? 'Pipeline de IA'} — ${flash.kind === 'completed' ? 'concluída' : 'falhou'}`;
  });

  constructor() {
    this.activity.start();
  }
}
